import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import InfoRounded from '@mui/icons-material/InfoRounded';
import "../styles.scss";
import "./DateInput.scss";

const PLACEHOLDERS = { day: 'DD', month: 'MM', year: 'YYYY' };
const MAX_LENGTHS = { day: 2, month: 2, year: 4 };
const ORDER = ['day', 'month', 'year'];

const pad2 = (v) => String(v).padStart(2, '0');

function daysInMonth(month, year) {
    const m = parseInt(month, 10);
    const y = parseInt(year, 10);
    if (!m || m < 1 || m > 12) return 31;
    if (y && year && year.length === 4) return new Date(y, m, 0).getDate();
    if ([4, 6, 9, 11].includes(m)) return 30;
    if (m === 2) return 29;
    return 31;
}

function clampDay(raw, month, year) {
    const v = parseInt(raw, 10);
    if (isNaN(v) || v === 0) return '';
    const max = daysInMonth(month, year);
    if (v < 1) return '';
    if (v > max) return pad2(max);
    return pad2(v);
}

function clampMonth(raw) {
    const v = parseInt(raw, 10);
    if (isNaN(v) || v === 0) return '';
    if (v > 12) return '12';
    return pad2(v);
}

function parseExternal(value) {
    if (!value) return { day: '', month: '', year: '' };
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return { day: '', month: '', year: '' };
    return { day: match[3], month: match[2], year: match[1] };
}

function isValidSegments(seg) {
    if (!seg.day || !seg.month || !seg.year || seg.year.length !== 4) return false;
    const d = parseInt(seg.day, 10);
    const mo = parseInt(seg.month, 10);
    const y = parseInt(seg.year, 10);
    if (!d || !mo || !y) return false;
    if (mo < 1 || mo > 12) return false;
    const max = daysInMonth(seg.month, seg.year);
    return d >= 1 && d <= max;
}

function formatExternal(seg) {
    if (!isValidSegments(seg)) return '';
    return `${seg.year}-${pad2(parseInt(seg.month, 10))}-${pad2(parseInt(seg.day, 10))}`;
}

const DateInput = ({
    value,
    onInput,
    label,
    variant = 'light',
    error,
    disabled,
    helper,
    className,
    fullWidth,
}) => {
    const { t } = useTranslation('fragments');
    const resolvedLabel = label ?? t('common:fields.date');
    const ariaLabels = {
        day: t('common:time.day'),
        month: t('common:time.month'),
        year: t('common:time.year'),
    };
    const [segments, setSegments] = useState(() => parseExternal(value));
    const containerRef = useRef(null);
    const dayRef = useRef(null);
    const monthRef = useRef(null);
    const yearRef = useRef(null);
    const refs = { day: dayRef, month: monthRef, year: yearRef };

    useEffect(() => {
        const active = typeof document !== 'undefined' ? document.activeElement : null;
        if (containerRef.current && active && containerRef.current.contains(active)) return;
        setSegments(parseExternal(value));
    }, [value]);

    const emit = useCallback((seg) => {
        if (onInput) onInput(formatExternal(seg));
    }, [onInput]);

    const focusSegment = (name) => {
        const node = refs[name].current;
        if (!node) return;
        node.focus();
        requestAnimationFrame(() => {
            try { node.setSelectionRange(0, node.value.length); } catch (_) { /* noop */ }
        });
    };

    const focusNext = (name) => {
        const idx = ORDER.indexOf(name);
        if (idx >= 0 && idx < ORDER.length - 1) focusSegment(ORDER[idx + 1]);
    };

    const focusPrev = (name) => {
        const idx = ORDER.indexOf(name);
        if (idx > 0) focusSegment(ORDER[idx - 1]);
    };

    const commit = (next, opts = {}) => {
        setSegments(next);
        emit(next);
        if (opts.advanceFrom) {
            setTimeout(() => focusNext(opts.advanceFrom), 0);
        }
    };

    const reclampDayIfNeeded = (next) => {
        if (!next.day) return next;
        const max = daysInMonth(next.month, next.year);
        if (parseInt(next.day, 10) > max) {
            return { ...next, day: pad2(max) };
        }
        return next;
    };

    const handleChange = (name) => (e) => {
        const cleaned = e.target.value.replace(/\D/g, '').slice(0, MAX_LENGTHS[name]);
        const next = { ...segments };

        if (name === 'day') {
            const max = daysInMonth(segments.month, segments.year);
            if (cleaned.length === 2) {
                next.day = clampDay(cleaned, segments.month, segments.year);
                commit(next, { advanceFrom: 'day' });
                return;
            }
            if (cleaned.length === 1) {
                const first = parseInt(cleaned, 10);
                if (first > 0 && first * 10 > max) {
                    next.day = pad2(first);
                    commit(next, { advanceFrom: 'day' });
                    return;
                }
                next.day = cleaned;
            } else {
                next.day = '';
            }
        } else if (name === 'month') {
            if (cleaned.length === 2) {
                next.month = clampMonth(cleaned);
                commit(reclampDayIfNeeded(next), { advanceFrom: 'month' });
                return;
            }
            if (cleaned.length === 1) {
                const first = parseInt(cleaned, 10);
                if (first >= 2) {
                    next.month = pad2(first);
                    commit(reclampDayIfNeeded(next), { advanceFrom: 'month' });
                    return;
                }
                next.month = cleaned;
            } else {
                next.month = '';
            }
        } else if (name === 'year') {
            next.year = cleaned;
            if (cleaned.length === 4) {
                commit(reclampDayIfNeeded(next));
                return;
            }
        }

        commit(next);
    };

    const handleKeyDown = (name) => (e) => {
        const target = e.target;
        const atStart = target.selectionStart === 0 && target.selectionEnd === 0;
        const atEnd = target.selectionStart === target.value.length
            && target.selectionEnd === target.value.length;

        if (e.key === 'Backspace') {
            if (target.value === '' && ORDER.indexOf(name) > 0) {
                e.preventDefault();
                focusPrev(name);
            }
            return;
        }

        if (e.key === 'ArrowLeft' && atStart) {
            e.preventDefault();
            focusPrev(name);
            return;
        }

        if (e.key === 'ArrowRight' && atEnd) {
            e.preventDefault();
            focusNext(name);
            return;
        }

        if (e.key === '/' || e.key === '-' || e.key === '.' || e.key === ' ') {
            e.preventDefault();
            const current = segments[name];
            const next = { ...segments };
            if (name === 'day' && current) {
                next.day = clampDay(current, segments.month, segments.year);
            } else if (name === 'month' && current) {
                next.month = clampMonth(current);
            }
            const reclamped = reclampDayIfNeeded(next);
            setSegments(reclamped);
            emit(reclamped);
            focusNext(name);
        }
    };

    const handleFocus = () => (e) => {
        try { e.target.setSelectionRange(0, e.target.value.length); } catch (_) { /* noop */ }
    };

    const handleBlur = (name) => () => {
        const current = segments[name];
        if (current && current.length === 1) {
            let next = { ...segments };
            if (name === 'day') next.day = clampDay(current, segments.month, segments.year);
            else if (name === 'month') next.month = clampMonth(current);
            next = reclampDayIfNeeded(next);
            setSegments(next);
            emit(next);
        }

        setTimeout(() => {
            if (!containerRef.current) return;
            const active = document.activeElement;
            if (active && containerRef.current.contains(active)) return;
            const incoming = parseExternal(value);
            const incomingIso = formatExternal(incoming);
            const currentIso = formatExternal(segments);
            if (!currentIso && incomingIso) {
                setSegments(incoming);
            }
        }, 0);
    };

    const containerClass = [
        'CustomInput_container',
        'date-input-container',
        disabled ? 'disabled' : '',
        error ? 'error' : '',
        formatExternal(segments) ? 'has-value' : '',
        variant === 'large' ? 'large' : 'small',
        fullWidth ? 'full-width' : '',
        className || '',
    ].filter(Boolean).join(' ');

    const renderSegment = (name) => {
        const segValue = segments[name];
        const placeholderText = PLACEHOLDERS[name];
        const maxLength = MAX_LENGTHS[name];
        const isEmpty = segValue === '';
        const hintText = isEmpty
            ? placeholderText
            : '0'.repeat(Math.max(0, maxLength - segValue.length));

        return (
            <span
                key={name}
                className={`date-input-segment-wrap date-input-segment-wrap--${name}`}
                onMouseDown={(e) => {
                    if (e.target.tagName !== 'INPUT') {
                        e.preventDefault();
                        focusSegment(name);
                    }
                }}
            >
                <span className="date-input-segment-display" aria-hidden="true">
                    <span className="date-input-segment-hint">{hintText}</span>
                    <span className="date-input-segment-typed">{segValue}</span>
                </span>
                <input
                    ref={refs[name]}
                    type="text"
                    className={`date-input-segment date-input-segment--${name}`}
                    value={segValue}
                    inputMode="numeric"
                    maxLength={maxLength}
                    autoComplete="off"
                    disabled={disabled}
                    aria-label={ariaLabels[name]}
                    onChange={handleChange(name)}
                    onKeyDown={handleKeyDown(name)}
                    onFocus={handleFocus(name)}
                    onBlur={handleBlur(name)}
                />
            </span>
        );
    };

    return (
        <div ref={containerRef} className={containerClass}>
            {resolvedLabel && <span className="CustomInput_label">{resolvedLabel}</span>}

            <div className={`CustomInput_platform-input-base date-input-base ${formatExternal(segments) ? 'has-value' : ''}`.trim()}>
                <div className="date-input-segments">
                    {renderSegment('day')}
                    <span className="date-input-separator">/</span>
                    {renderSegment('month')}
                    <span className="date-input-separator">/</span>
                    {renderSegment('year')}
                </div>
            </div>

            {helper && (
                <div className="CustomInput_bottom">
                    <span className="CustomInput_helper">
                        <InfoRounded />
                        <span>{helper}</span>
                    </span>
                </div>
            )}
        </div>
    );
};

export default DateInput;
